'use client'

import * as React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTheme } from 'next-themes'
import { Bell, Moon, Sun, Search, LogOut, User, Settings } from 'lucide-react'
import { signOut } from 'firebase/auth'
import { SidebarTrigger } from 'components/ui/sidebar'
import { Button } from 'components/ui/button'
import { Separator } from 'components/ui/separator'
import { Badge } from 'components/ui/badge'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from 'components/ui/dropdown-menu'
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover'
import { Input } from 'components/ui/input'
import { Avatar, AvatarFallback } from 'components/ui/avatar'
import { ScrollArea } from 'components/ui/scroll-area'
import { mockNotifications } from 'lib/mock-data'
import { Breadcrumbs } from './breadcrumbs'
import { auth } from '../../firebase'
import { toast } from 'sonner'
import { useUser } from 'components/context/user-context'
import { getAccessLevel } from 'lib/access'

export function Topbar() {
  const { theme, setTheme } = useTheme()
  const navigate = useNavigate()
  const { user, loading } = useUser()
  const currentUser = React.useMemo(() => ({
    name: user?.fullName || (loading ? 'Loading...' : 'User'),
    email: user?.email || '',
    role: user?.userType || '',
  }), [user, loading])
  const accessLevel = getAccessLevel(user?.userType)
  const unreadCount = mockNotifications.filter((n) => !n.isRead).length

  async function handleSignOut() {
    try {
      await signOut(auth)
      toast.success('Signed out successfully.')
      navigate('/login')
    } catch {
      toast.error('Unable to sign out. Please try again.')
    }
  }

  function getInitials(name) {
    if (!name) return 'U'
    return name
      .trim()
      .split(' ')
      .filter(Boolean)
      .map((part) => part[0])
      .join('')
      .slice(0, 2)
      .toUpperCase()
  }

  const showSettings = accessLevel !== 'limited'

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-card px-4">
      <SidebarTrigger className="text-muted-foreground hover:text-foreground" />
      <Separator orientation="vertical" className="h-5" />
      <Breadcrumbs />

      <div className="ml-auto flex items-center gap-1">
        {/* Search */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="size-8 text-muted-foreground hover:text-foreground">
              <Search className="size-4" />
              <span className="sr-only">Search</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-3" align="end">
            <Input placeholder="Search projects, reports, indicators..." className="h-9" />
            <p className="mt-2 text-xs text-muted-foreground">
              Type to search across all modules
            </p>
          </PopoverContent>
        </Popover>

        {/* Notifications */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="relative size-8 text-muted-foreground hover:text-foreground">
              <Bell className="size-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-primary-foreground">
                  {unreadCount}
                </span>
              )}
              <span className="sr-only">Notifications</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-96 p-0" align="end">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
              <Badge variant="secondary" className="text-[10px]">
                {unreadCount} unread
              </Badge>
            </div>
            <ScrollArea className="h-80">
              <div className="flex flex-col">
                {mockNotifications.map((notif) => (
                  <Link
                    key={notif.id}
                    to={notif.link}
                    className={`flex flex-col gap-1 border-b border-border px-4 py-3 transition-colors hover:bg-muted ${
                      !notif.isRead ? 'bg-primary/5' : ''
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      {!notif.isRead && (
                        <span className="mt-1.5 size-2 shrink-0 rounded-full bg-primary" />
                      )}
                      <div className="flex flex-col gap-0.5">
                        <p className="text-xs text-foreground leading-relaxed">{notif.message}</p>
                        <span className="text-[10px] text-muted-foreground">
                          {new Date(notif.createdAt).toLocaleDateString('en-KE', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </ScrollArea>
            <div className="border-t border-border p-2">
              <Button variant="ghost" className="w-full text-xs text-muted-foreground" size="sm">
                View all notifications
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        {/* Theme Toggle */}
        <Button
          variant="ghost"
          size="icon"
          className="size-8 text-muted-foreground hover:text-foreground"
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        >
          <Sun className="size-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
          <Moon className="absolute size-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
          <span className="sr-only">Toggle theme</span>
        </Button>

        <Separator orientation="vertical" className="mx-1 h-5" />

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="gap-2 px-2 h-8">
              <Avatar className="size-6">
                <AvatarFallback className="bg-primary text-primary-foreground text-[10px] font-semibold">
                  {getInitials(currentUser.name)}
                </AvatarFallback>
              </Avatar>
              <span className="hidden text-xs font-medium text-foreground md:inline-block">
                {currentUser.name.split(' ').slice(0, 2).join(' ')}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col gap-1">
                <p className="text-sm font-medium text-foreground">{currentUser.name}</p>
                <p className="text-xs text-muted-foreground">{currentUser.email}</p>
                {currentUser.role ? (
                  <Badge variant="outline" className="w-fit text-[10px] capitalize mt-1">
                    {currentUser.role}
                  </Badge>
                ) : null}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/dashboard/profile" className="cursor-pointer">
                <User className="mr-2 size-4" />
                Profile
              </Link>
            </DropdownMenuItem>
            {showSettings ? (
              <DropdownMenuItem asChild>
                <Link to="/dashboard/settings" className="cursor-pointer">
                  <Settings className="mr-2 size-4" />
                  Settings
                </Link>
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive cursor-pointer"
              onSelect={(event) => {
                event.preventDefault()
                handleSignOut()
              }}
            >
              <LogOut className="mr-2 size-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
